/**
 * EditableModal Component
 * Generic modal for editing any content type
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FiX } from 'react-icons/fi';

const EditableModal = ({ 
  isOpen, 
  isCreating, 
  onClose, 
  onSave, 
  title = null,
  children,
  saveLabel = null,
  cancelLabel = 'Cancel',
  maxWidth = '600px'
}) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSave(); // Call without passing the event
  };

  return (
    <ModalOverlay onClick={handleBackdropClick}>
      <Modal $maxWidth={maxWidth}>
        <ModalHeader>
          <h3>{title || (isCreating ? 'Add Content' : 'Edit Content')}</h3>
          <CloseButton onClick={onClose} aria-label="Close modal">
            <FiX />
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          {children}
        </ModalBody>
        
        <ModalFooter>
          <ButtonCancel onClick={onClose}>
            {cancelLabel || 'Cancel'}
          </ButtonCancel>
          <ButtonSave onClick={handleSave}>
            {saveLabel || (isCreating ? 'Create' : 'Save')}
          </ButtonSave>
        </ModalFooter>
      </Modal>
    </ModalOverlay>
  );
};

// Styled Components

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
  overflow-y: auto;
`;

const Modal = styled.div`
  background: var(--bg-primary);
  border-radius: 0.5rem;
  max-width: ${props => props.$maxWidth};
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 
              0 10px 10px -5px rgba(0, 0, 0, 0.04);
  
  @media (max-width: 768px) {
    max-height: 95vh;
    margin: auto;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
  
  h3 {
    margin: 0;
    color: var(--text-primary);
    font-size: 1.25rem;
    font-weight: 600;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--text-primary);
    background: var(--bg-tertiary);
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    color: var(--text-primary);
    font-weight: 600;
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  input, textarea, select {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 0.375rem;
    background: var(--bg-secondary);
    color: var(--text-primary);
    font-family: inherit;
    font-size: 1rem;
    margin-bottom: 1rem;
    transition: all 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: var(--accent-primary);
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
    }
    
    &::placeholder {
      color: var(--text-tertiary);
    }
  }
  
  textarea {
    min-height: 100px;
    resize: vertical;
    font-family: inherit;
    line-height: 1.5;
  }
  
  select {
    cursor: pointer;
  }
  
  input[type="number"] {
    -moz-appearance: textfield;
    
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
  
  input[type="range"] {
    padding: 0.5rem 0;
    cursor: pointer;
    
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: var(--accent-primary);
      cursor: pointer;
    }
    
    &::-moz-range-thumb {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: var(--accent-primary);
      cursor: pointer;
      border: none;
    }
  }
  
  /* Helper text */
  small {
    display: block;
    margin-top: -0.5rem;
    margin-bottom: 1rem;
    color: var(--text-secondary);
    font-size: 0.875rem;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid var(--border-color);
  justify-content: flex-end;
  background: var(--bg-secondary);
  
  @media (max-width: 480px) {
    flex-direction: column-reverse;
    
    button {
      width: 100%;
    }
  }
`;

const ButtonCancel = styled.button`
  padding: 0.75rem 1.5rem;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
    border-color: var(--text-secondary);
  }
`;

const ButtonSave = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--accent-gradient);
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

EditableModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isCreating: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  saveLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  maxWidth: PropTypes.string
};

export default EditableModal;
